import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { CreateCalendarEntryData, UpdateCalendarEntryData, calendarApi, schoolYearsApi } from '@/services';

const ADMIN_CALENDAR_BASE = ['admin', 'calendar'] as const;

export const adminCalendarKeys = {
  all: ADMIN_CALENDAR_BASE,
  list: (page = 1, limit = 500, category?: string, year?: number, schoolYearId?: string) =>
    [...ADMIN_CALENDAR_BASE, 'list', { page, limit, category, year, schoolYearId }] as const,
  schoolYears: [...ADMIN_CALENDAR_BASE, 'school-years'] as const,
};

export function useAdminCalendarListQuery(
  page = 1,
  limit = 500,
  category?: string,
  year?: number,
  schoolYearId?: string
) {
  return useQuery({
    queryKey: adminCalendarKeys.list(page, limit, category, year, schoolYearId),
    queryFn: () => calendarApi.getAll(page, limit, category, year, schoolYearId),
  });
}

export function useAdminCalendarEntryQuery(id?: string) {
  return useQuery({
    queryKey: [...adminCalendarKeys.all, 'detail', id] as const,
    queryFn: () => calendarApi.getById(id as string),
    enabled: Boolean(id),
  });
}

export function useAdminCalendarCategoriesQuery() {
  return useQuery({
    queryKey: [...adminCalendarKeys.all, 'categories'] as const,
    queryFn: () => calendarApi.getManagedCategories(),
  });
}

export function useAdminCalendarSchoolYearsQuery() {
  return useQuery({
    queryKey: adminCalendarKeys.schoolYears,
    queryFn: () => schoolYearsApi.list(),
  });
}

export function useCreateCalendarEntryMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCalendarEntryData) => calendarApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminCalendarKeys.all });
    },
  });
}

export function useUpdateCalendarEntryMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCalendarEntryData }) => calendarApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: adminCalendarKeys.all });
      queryClient.invalidateQueries({
        queryKey: [...adminCalendarKeys.all, 'detail', variables.id],
      });
    },
  });
}

export function useDeleteCalendarEntryMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => calendarApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminCalendarKeys.all });
    },
  });
}

export function useToggleCalendarPublishedMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => calendarApi.togglePublished(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: adminCalendarKeys.all });
      queryClient.invalidateQueries({
        queryKey: [...adminCalendarKeys.all, 'detail', id],
      });
    },
  });
}

export function useCreateCalendarCategoryMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string; color?: string }) => calendarApi.createCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminCalendarKeys.all });
    },
  });
}

export function useUpdateCalendarCategoryMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, name, color }: { id: string; name: string; color?: string }) =>
      calendarApi.updateCategory(id, { name, color }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminCalendarKeys.all });
    },
  });
}

export function useDeleteCalendarCategoryMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => calendarApi.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminCalendarKeys.all });
    },
  });
}
